import { Button, ButtonGroup } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { filter, map, sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  FormControls,
  FormikSelect,
  FormikSelectOption,
  usePostSavePrompt,
  useUnsavedChangesGuard,
} from 'components';
import { useCurrencies, useTouchErrorsOnLoad } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { ShopP2PProviders } from '../../ShopP2PProviders';
import { ShopP2PProvidersCurrencyExchanges } from '../../ShopP2PProvidersCurrencyExchanges';
import {
  useManageShop,
  ManageShopAccordion,
  ManageShopTabActions,
} from '../components';
import { useShopSubmit } from '../hooks';

type CascadeValues = {
  cascadeId: string;
  p2pProvidersConfigs: any[];
  p2pProvidersCurrencyExchanges: any[];
};

type Props = {
  cascadeOptions: FormikSelectOption[];
};

export const CascadeTab: React.FC<Props> = ({ cascadeOptions }) => {
  const { shopId, shopData, isNew } = useManageShop();
  const { onSavedSuccess } = usePostSavePrompt();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const { getCurrencyExchange } = useCurrencies();

  const { handleSubmit: submitShop } = useShopSubmit({
    shopId,
    onSuccess: onSavedSuccess,
  });

  const initialValues: CascadeValues = useMemo(() => {
    if (shopData) {
      return {
        cascadeId: shopData.cascadeId || '',
        p2pProvidersConfigs: sortBy(
          shopData.p2pProvidersConfigs || [],
          'priority',
        ),
        p2pProvidersCurrencyExchanges:
          shopData.p2pProvidersCurrencyExchanges || [],
      };
    }
    return {
      cascadeId: '',
      p2pProvidersConfigs: [],
      p2pProvidersCurrencyExchanges: [],
    };
  }, [shopData]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        p2pProvidersConfigs: Yup.array().of(
          Yup.object().shape({
            p2pProviderId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        p2pProvidersCurrencyExchanges: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: submitShop,
  });

  useTouchErrorsOnLoad(formik, !isNew && shopData ? initialValues : undefined);

  useUnsavedChangesGuard(formik);

  const [cascadeModeEnabled, setCascadeModeEnabled] = useState(false);

  useEffect(() => {
    setCascadeModeEnabled(!!initialValues.cascadeId);
  }, [initialValues.cascadeId]);

  const isCascadeMode = useMemo(() => cascadeModeEnabled, [cascadeModeEnabled]);

  const handleToggleCascadeMode = useCallback(() => {
    const newMode = !cascadeModeEnabled;
    setCascadeModeEnabled(newMode);
    if (!newMode) {
      formik.setFieldValue('cascadeId', '');
    }
  }, [cascadeModeEnabled, formik]);

  const getAddedP2PProvidersAssetCurrencies = useCallback(
    () => [
      ...(shopData?.assetCurrencyId ? [shopData.assetCurrencyId] : []),
      ...filter(
        map(
          formik.values.p2pProvidersCurrencyExchanges,
          (exchange) => getCurrencyExchange(exchange.id)?.assetCurrencyId || '',
        ),
        (id) => !!id,
      ),
    ],
    [
      formik.values.p2pProvidersCurrencyExchanges,
      getCurrencyExchange,
      shopData?.assetCurrencyId,
    ],
  );

  return (
    <FormikProvider value={formik}>
      <Form>
        {/* Payin Cascade */}
        <ManageShopAccordion
          title={t('sections.cascade_payin')}
          defaultExpanded
        >
          <FormControls>
            <FormControls sx={{ mb: 2 }}>
              <ButtonGroup sx={{ display: 'flex' }}>
                <Button
                  variant={!isCascadeMode ? 'contained' : 'outlined'}
                  onClick={handleToggleCascadeMode}
                  sx={{ flex: 1 }}
                >
                  {t('sections.p2p_providers.title')}
                </Button>
                <Button
                  variant={isCascadeMode ? 'contained' : 'outlined'}
                  onClick={handleToggleCascadeMode}
                  sx={{ flex: 1 }}
                >
                  {t('fields.cascade')}
                </Button>
              </ButtonGroup>
            </FormControls>
            {isCascadeMode && (
              <FormikSelect
                label={t('fields.cascade')}
                name="cascadeId"
                options={cascadeOptions}
                size="small"
              />
            )}
            {!isCascadeMode && (
              <ShopP2PProviders
                assetCurrencyIds={getAddedP2PProvidersAssetCurrencies()}
                tradeMethodsIds={map(
                  shopData?.payinTradeMethodsConfigs || [],
                  (tradeMethod) => tradeMethod.tradeMethodId,
                )}
              />
            )}
            <ShopP2PProvidersCurrencyExchanges
              fiatCurrencyId={shopData?.fiatCurrencyId || ''}
              assetCurrencyIds={getAddedP2PProvidersAssetCurrencies()}
            />
          </FormControls>
        </ManageShopAccordion>

        <ManageShopTabActions />
      </Form>
    </FormikProvider>
  );
};
